import { useState, useEffect, useCallback, useRef } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import type { StatusValue } from 'types/spherePoints';

export type { StatusValue } from 'types/spherePoints';

interface StatusPointValue {
  status: StatusValue;
  perspective: string | null;
  dimensions: any[];
  factors: any[];
}

interface SpherePoint {
  id: string;
  sphereId: string;
  pointType: 'status';
  currentValue: StatusPointValue;
  validFrom: string;
  validUntil: string | null;
  computedAt: string;
  computedBy: string;
  version: number;
  active: boolean;
  metadata?: Record<string, any>;
}

interface UseSphereStatusResult {
  status: StatusValue | null;
  loading: boolean;
  error: string | null;
  point: SpherePoint | null;
  refetch: () => void;
}

/**
 * Hook to fetch sphere status from the graph service
 * Returns 'unknown' status if no status point is configured
 */
export function useSphereStatus(sphereId: string | undefined): UseSphereStatusResult {
  const apiClients = useApiClientsOrNull();
  const [status, setStatus] = useState<StatusValue | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [point, setPoint] = useState<SpherePoint | null>(null);
  const mountedRef = useRef(true);

  const fetchStatus = useCallback(async () => {
    if (!sphereId) {
      setStatus('unknown');
      setLoading(false);
      setError(null);
      setPoint(null);
      return;
    }
    if (!apiClients) {
      // Wait for api clients to be ready (auth still resolving)
      setLoading(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiClients.graphClient.get<{ point: SpherePoint | null }>(
        `/sphere/${encodeURIComponent(sphereId)}/points/status`
      );

      if (mountedRef.current) {
        // A sphere with no status point yet answers 200 with point: null.
        const statusPoint = data?.point ?? undefined;
        setPoint(statusPoint ?? null);
        setStatus(statusPoint?.currentValue?.status || 'unknown');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Error fetching sphere status:', err);
      if (mountedRef.current) {
        setError(err?.message || 'Failed to fetch status');
        setStatus('unknown');
        setLoading(false);
      }
    }
  }, [sphereId, apiClients]);

  useEffect(() => {
    mountedRef.current = true;
    fetchStatus();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchStatus]);

  return { status, loading, error, point, refetch: fetchStatus };
}
